// MAVIS Page Context — every admin page auto-publishes a live Context Object
// (route, selection(s), visible entities, filters, search, campus/semester,
// active workflow, recent actions, pending proposals, active recommendations)
// into a shared provider that the docked assistant reads on every turn.
//
// Design goals:
//   * Zero-friction on pages that don't opt in — the route alone gives a
//     usable section + label.
//   * Additive — pages call `usePublishPageContext` with only the fields they
//     know; unspecified fields inherit from the auto-derived defaults.
//   * Serializable — the whole object is JSON-safe so it can ride along with
//     every model call.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export interface PageSelection {
  /** Domain kind — instructor, class, registration, vendor, payroll_period, etc. */
  kind: string;
  /** Stable id if available. */
  id?: string | null;
  /** Human label shown in the breadcrumb ("Kohl Knopp"). */
  label: string;
  /** Optional short pronoun hint ("him", "her", "them", "it"). */
  pronoun?: string;
  /** Free-form record fingerprint the model can use (email, status, etc.). */
  attributes?: Record<string, unknown>;
}

export interface VisibleEntitySummary {
  /** Kind of the collection ("instructors", "registrations", ...). */
  kind: string;
  /** How many rows are currently rendered on screen after filters. */
  count: number;
  /** Up to N sample IDs/labels so the model can reason about "these". */
  sample?: Array<{ id?: string | null; label: string }>;
  /** Optional total (before filters), if known. */
  total?: number;
}

export interface RecentAction {
  id: string;
  /** Verb-noun summary — "Approved instructor proposal", "Marked #3 done". */
  summary: string;
  /** Where it happened. */
  section: string;
  /** ISO timestamp. */
  at: string;
  /** Optional target for follow-up questions ("this instructor"). */
  target?: { kind: string; id?: string | null; label?: string };
}

export interface PageContextValue {
  // -------- Location --------
  section: string;
  sectionLabel: string;
  route?: string;

  // -------- Selection & visibility --------
  /** Primary focused record (drives pronoun resolution). */
  selection?: PageSelection | null;
  /** Additional selected records (multi-select tables). */
  selections?: PageSelection[];
  /** Summaries of visible tables/lists on the page. */
  visible?: VisibleEntitySummary[];

  // -------- Filters & search --------
  filters?: Record<string, string | number | boolean | null | undefined>;
  search?: string;

  // -------- Business scope --------
  campus?: string;
  semester?: string;
  month?: string;

  // -------- Workflow / async state --------
  activeWorkflow?: { name: string; step?: number; totalSteps?: number } | null;
  pendingProposals?: number;
  activeRecommendations?: number;

  // -------- Operational snapshot (authoritative, page-owned) --------
  // When the page already has the operational data for the current project
  // (unpaid/paid registrations, waitlist, interest, revenue, instructor,
  // etc.), publish it here. MAVIS treats this as the source of truth and
  // must NOT re-query or fall back to the recommendation cache.
  operationalSnapshot?: OperationalSnapshot | null;

  // -------- Freeform note (dialog open, etc.) --------
  note?: string;
}

export interface OperationalSnapshot {
  /** Human label for the project ("Chess Club — Session 2"). */
  projectLabel?: string;
  classId?: string | null;
  sessionId?: string | null;
  instructor?: { id?: string | null; label: string } | null;
  unpaidRegistrations?: Array<{ id: string; student: string; parent?: string; amount?: number; note?: string }>;
  paidRegistrations?: Array<{ id: string; student: string; parent?: string; amount?: number }>;
  waitlist?: Array<{ id: string; student: string; parent?: string }>;
  interest?: Array<{ id: string; student: string; parent?: string }>;
  revenueCents?: number;
  /** Free-form extra facts. */
  extra?: Record<string, unknown>;
}


interface Ctx {
  ctx: PageContextValue;
  /** Merge an override on top of the auto route-derived context. */
  set: (patch: Partial<PageContextValue>) => void;
  /** Clear a caller's contribution. */
  clear: () => void;
  // Recent-actions ring buffer (last 20). Any page or workflow can record
  // an action; MAVIS ships them in the Context Object so it can answer
  // "what did I just do?" without re-querying the database.
  recentActions: RecentAction[];
  recordAction: (a: Omit<RecentAction, "id" | "at"> & { at?: string }) => void;
  clearRecentActions: () => void;
}

const PageCtx = createContext<Ctx | null>(null);

// Route → (section, label). Matched with startsWith so nested routes work.
const ROUTES: Array<[string, string, string]> = [
  ["/admin/instructors", "instructors", "Instructors"],
  ["/admin/registrations", "registrations", "Registrations"],
  ["/admin/classes", "classes", "Classes"],
  ["/admin/payroll", "payroll", "Payroll"],
  ["/admin/popup", "popup", "Pop-Up"],
  ["/admin/waiting", "waiting", "Waiting On"],
  ["/admin/action-center", "action_center", "Action Center"],
  ["/admin/archived", "archived", "Archived Alerts"],
  ["/admin/knowledge", "knowledge", "Knowledge Vault"],
  ["/admin/reports", "reports", "Reports"],
  ["/admin/settings", "settings", "Settings"],
  ["/admin/backlog", "backlog", "Improvement Backlog"],
  ["/admin", "dashboard", "Dashboard"],
];

function routeToSection(pathname: string): { section: string; sectionLabel: string } {
  for (const [prefix, section, label] of ROUTES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/") || pathname.startsWith(prefix + "?")) {
      return { section, sectionLabel: label };
    }
  }
  return { section: "admin", sectionLabel: "Admin" };
}

function deriveSemester(d = new Date()): string {
  const m = d.getMonth() + 1;
  if (m >= 1 && m <= 4) return `Spring ${d.getFullYear()}`;
  if (m >= 5 && m <= 8) return `Summer ${d.getFullYear()}`;
  return `Fall ${d.getFullYear()}`;
}

const RECENT_ACTIONS_KEY = "eos:recentActions:v1";
const RECENT_ACTIONS_MAX = 20;

function loadRecentActions(): RecentAction[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(RECENT_ACTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentAction[]) : [];
  } catch {
    return [];
  }
}

export function PageContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user } = useAuth();
  const [override, setOverride] = useState<Partial<PageContextValue>>({});
  const [recentActions, setRecentActions] = useState<RecentAction[]>(() => loadRecentActions());

  // Reset caller contribution when the route changes so stale selection from
  // one page doesn't leak into the next.
  useEffect(() => {
    setOverride({});
  }, [location.pathname]);

  // Persist recent actions across route changes (same tab session).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(RECENT_ACTIONS_KEY, JSON.stringify(recentActions));
    } catch { /* quota — non-fatal */ }
  }, [recentActions]);

  const ctx = useMemo<PageContextValue>(() => {
    const auto = routeToSection(location.pathname);
    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "long", year: "numeric" });
    return {
      ...auto,
      route: location.pathname,
      campus: (user?.user_metadata?.campus as string | undefined) ?? "Silver Sage Homeschool Hub",
      semester: deriveSemester(now),
      month,
      ...override,
    };
  }, [location.pathname, override, user]);

  const recordAction = useCallback<Ctx["recordAction"]>((a) => {
    setRecentActions((prev) => {
      const entry: RecentAction = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        at: a.at ?? new Date().toISOString(),
        summary: a.summary,
        section: a.section,
        target: a.target,
      };
      return [entry, ...prev].slice(0, RECENT_ACTIONS_MAX);
    });
  }, []);

  const clearRecentActions = useCallback(() => setRecentActions([]), []);

  const value = useMemo<Ctx>(
    () => ({
      ctx: { ...ctx },
      set: (patch) => setOverride((prev) => ({ ...prev, ...patch })),
      clear: () => setOverride({}),
      recentActions,
      recordAction,
      clearRecentActions,
    }),
    [ctx, recentActions, recordAction, clearRecentActions],
  );

  return <PageCtx.Provider value={value}>{children}</PageCtx.Provider>;
}

const NOOP: Ctx = {
  ctx: { section: "admin", sectionLabel: "Admin" },
  set: () => {},
  clear: () => {},
  recentActions: [],
  recordAction: () => {},
  clearRecentActions: () => {},
};

export function usePageContext(): Ctx {
  const v = useContext(PageCtx);
  return v ?? NOOP;
}

/**
 * Publish enrichment for the current page. Call inside a page component or a
 * drawer/dialog that opens with a specific record selected. Anything you
 * don't specify falls back to the route-derived defaults.
 */
export function usePublishPageContext(patch: Partial<PageContextValue> | null | undefined) {
  const { set, clear } = usePageContext();
  const key = JSON.stringify(patch ?? {});
  const clearRef = useRef(clear);
  clearRef.current = clear;
  useEffect(() => {
    if (!patch) return;
    set(patch);
    return () => clearRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Convenience hook to record a recent admin action into the ring buffer. */
export function useRecordAction() {
  return usePageContext().recordAction;
}

/** Serialize the page context into the compact JSON the edge function expects. */
export function serializePageContext(
  ctx: PageContextValue,
  extras?: { recentActions?: RecentAction[] },
) {
  return {
    section: ctx.section,
    section_label: ctx.sectionLabel,
    route: ctx.route ?? null,
    selection: ctx.selection ?? null,
    selections: ctx.selections ?? null,
    visible: ctx.visible ?? null,
    filters: ctx.filters ?? null,
    search: ctx.search ?? null,
    campus: ctx.campus ?? null,
    semester: ctx.semester ?? null,
    month: ctx.month ?? null,
    active_workflow: ctx.activeWorkflow ?? null,
    pending_proposals: ctx.pendingProposals ?? null,
    active_recommendations: ctx.activeRecommendations ?? null,
    operational_snapshot: ctx.operationalSnapshot ?? null,
    note: ctx.note ?? null,
    recent_actions: extras?.recentActions?.slice(0, 10) ?? null,
  };
}

